<script lang="ts" setup>
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import StoryCard from "@/components/mobile/story/story-chapter/StoryCard.vue";
import { Image } from "vant";
import { computed, onBeforeMount, ref, nextTick } from "vue";
import StoryChapterList from "@/components/mobile/story/story-chapter/StoryChapterList.vue";
import { useRoute, useRouter } from "vue-router";
import { useStoryBook } from "@/stores/story-book";
import { useMate } from "@/stores/mate";
import { getMomentsFromChapter, useStory } from "@/stores/story";
import ThreadItem from "@/components/mobile/ThreadItem.vue";
import { extractImgUrlList } from "@/modules/editor/editor-json-utils";
import { isImage } from "@/modules/common";
import VideoIcon from "@/components/icon/VideoIcon.vue";
import MateMediaPreview from "@/components/mobile/overlay/MateMediaPreview.vue";
import { Media, Story, StoryBook, Reply, Moment } from "@/types/entity";
import StoryBookListItem from "@/components/mobile/story/story-book/StoryBookListItem.vue";
import { ReplyInputInstance } from "../../reply/ReplyInput.vue";
import { useReply } from "@/stores/reply";
import { ReplyEntityType } from "@/enums";
// State
const route = useRoute();
const router = useRouter();
const replyStore = useReply();
const mateStore = useMate();
const currentTab = ref("chapter");
const loading = ref(false);
const storyBookStore = useStoryBook();
const storyStore = useStory();
const scrollContainer = ref<HTMLElement>();

const onScroll = (e: any) => {
  storyBookStore.storyScrollMap[route.params.uuid as string] =
    e.target.scrollTop;
};

const storyBook = computed(() => {
  const storyBook = storyBookStore.storyBookMap[route.params.uuid as string];
  if (!storyBook) return;
  const { reply } = storyBook;
  replyTarget.value = {
    ...reply,
    mate: storyBook.mate,
  };
  //@ts-ignore
  toOperateMoment.value = { reply };
  return storyBook;
});
const storyList = computed(() => {
  if (
    !storyBook.value ||
    !storyStore.storyBookId2StoryUuids[storyBook.value.id!]
  ) {
    return [];
  }
  return storyStore.storyBookId2StoryUuids[storyBook.value.id!].map(
    (uuid) => storyStore.storyMap[uuid]
  );
});
const isOwner = computed(() => {
  if (!storyBook.value || !mateStore.userMate) {
    return false;
  }
  return storyBook.value?.mate.id === mateStore.userMate.id;
});
const author = computed(() => storyBook.value?.mate);
const momentList = computed(() => {
  const uniqueMomentMap = storyList.value
    .map((story) => getMomentsFromChapter(story.storyChapterzz))
    .reduce((acc, cur) => {
      // 去重
      cur.forEach((moment) => {
        if (!acc.has(moment.id)) {
          acc.set(moment.id, moment);
        }
      });
      return acc;
    }, new Map());
  return Array.from(uniqueMomentMap.values()).sort(
    (a, b) => new Date(b.dtCreate).getTime() - new Date(a.dtCreate).getTime()
  );
});
const mediaList = computed(() => {
  const mediaMap = storyList.value
    .map((story) => extractImgUrlList(storyStore.getJson(story.text)))
    .reduce((acc, cur) => {
      cur.forEach((url) => {
        if (!acc.has(url)) {
          acc.set(url, url);
        }
      });
      return acc;
    }, new Map());
  return Array.from(mediaMap.values());
});
const showPreview = ref(false);
const startPosition = ref(0);
const previewMediaList = computed(() => {
  return mediaList.value.map((url) => ({ url } as MediaWithMateMediaId));
});
const authorStoryList = ref<StoryBook[]>([]);
const moreStoryBookFromAuthor = computed(() => {
  return authorStoryList.value.filter(
    (item) => item.id !== storyBook.value?.id
  );
});

const storyList1 = ref<Story[]>([]);
const sortedStoryList = computed(() => {
  return [...storyList1.value].sort(
    (a, b) => new Date(b.order!).getTime() - new Date(a.order!).getTime()
  );
});

// const totalEmojiCount = ref(0);
// const totalReplyCount = ref(0);
// Lifecycle
const prepareData = async () => {
  const storyBookUuid = route.params.uuid as string;
  loading.value = true;
  try {
    // 1. 查询storyBook详情
    const storyBookItem = await storyBookStore.getOrFetchStoryItem(
      storyBookUuid
    );

    useReply().getOrFetchReplyTreeList(
      storyBookItem.id!,
      "App\\Models\\StoryBook"
    );

    if (isOwner.value) {
      // 2.1 查询story列表
      storyList1.value = await storyStore.getStoryListByStoryBook(
        storyBookItem
      );
    } else {
      // 2.1 查询story列表
      storyList1.value = await storyStore.getStoryListByStoryBook(
        storyBookItem,
        true,
        storyBookItem.mateId
      );
      // 2.2 查询作者的其他故事
      storyBookStore
        .fetchStoryBookListByMateId(storyBookItem.mateId, {
          page: 1,
          perPage: 5,
        })
        .then((list) => {
          authorStoryList.value = list;
        });
    }
  } finally {
    const { storyScrollMap } = storyBookStore;
    loading.value = false;
    if (storyScrollMap[storyBookUuid]) {
      nextTick(() => {
        scrollContainer.value!.scrollTop = storyScrollMap[storyBookUuid];
      });
    }
  }
};
onBeforeMount(() => {
  prepareData();
});

const onOtherStoryBookClick = async (storyBookUuid: string) => {
  await router.push({
    name: "storyBookHome",
    params: { uuid: storyBookUuid },
  });
  await prepareData();
};

const showMenu = ref(false);
const currentStory = ref<Story | null>(null);

const onGoTargetStory = async (uuid: string) => {
  showMenu.value = false;
  router.push({ name: "readStory", params: { uuid } });
};

const replyTarget = ref<Reply | null>(null); // 底部输入框的回复目标
const replyInputRef = ref<ReplyInputInstance | null>(null);
const showActions = ref(false);
const showEmoji = ref(0);
const toOperateMoment = ref<Moment | null>(null);
const toOperateReply = ref<Reply | null>(null);

const onReplyBtnClick = (replay: Reply) => {
  replyTarget.value = replay;
  replyInputRef.value?.focus();
};
const onReplyActionClick = (reply: Reply) => {
  toOperateReply.value = reply;
  showActions.value = true;
};

const replyCount = computed(
  () => replyStore.countOwnerMap[storyBook.value?.id!]
);
</script>
<template>
  <div class="h-full overflow-hidden">
    <div class="h-full grid grid-rows-[auto_1fr_auto] relative story-home">
      <threads-nav :enable-switch-mate="false" show-back :show-center="false" />
      <div
        v-if="loading || !storyBook"
        class="flex flex-col items-center justify-center h-[200px]"
      >
        <span class="loading loading-spinner"></span>
      </div>
      <div
        v-else
        ref="scrollContainer"
        class="h-full overflow-y-auto"
        @scroll="onScroll"
      >
        <story-card :isOwner="isOwner" :story-book="storyBook!" />
        <VanTabs
          v-model:active="currentTab"
          class="thread-tabs"
          offset-top="56"
          sticky
        >
          <VanTab :title="$t('story.tab.chapter')" name="chapter">
            <div class="flex flex-col pb-[60px]">
              <story-chapter-list
                :story-book="storyBook!"
                :story-list="storyList"
              />

              <div v-if="moreStoryBookFromAuthor.length > 0" class="p-2">
                <div class="font-bold">{{ $t("story.moreStory") }}</div>
                <div>
                  <story-book-list-item
                    v-for="item in moreStoryBookFromAuthor"
                    :key="item.id"
                    :story-book="item"
                    class="border-b last-of-type:border-b-0"
                    @click="onOtherStoryBookClick(item.uuid)"
                  />
                </div>
              </div>
            </div>
          </VanTab>
          <VanTab
            :title="`${$t(`Overview.storyFeedback`)}(${
              (storyBook?.reply.emojiVotezz.length || 0) +
                (replyCount?.replyCount || 0) >
              99
                ? '99+'
                : (storyBook?.reply.emojiVotezz.length || 0) +
                  (replyCount?.replyCount || 0)
            })`"
            class="h-full relative"
            name="feedback"
          >
            <div class="p-4">
              <div class="flex flex-col gap-2 pb-5 border-b mb-2">
                <reaction-mate :reply="storyBook?.reply" />
                <div class="flex w-full justify-between items-end">
                  <div class="flex gap-2 flex-wrap">
                    <div class="flex gap-1">
                      <!-- <Donate
                      v-if="storyBook"
                      :item="storyBook"
                      type="StoryBook"
                    />
                    <coin-vote
                      v-if="storyBook"
                      :item="storyBook"
                      :type="CoinBalanceRecordEntityType.StoryBook"
                    /> -->
                      <button class="btn btn-sm rounded" @click="showEmoji++">
                        <i class="iconify mingcute--thumb-up-line"></i>
                      </button>
                      <!-- @vue-ignore -->
                      <button
                        class="btn btn-sm rounded"
                        @click="()=>onReplyBtnClick({ ...storyBook.reply!, mate: storyBook.mate })"
                      >
                        {{ $t("Overview.comments") }}
                        <span v-if="replyCount?.replyCount > 0">
                          ({{ replyCount?.replyCount }})
                        </span>
                      </button>
                    </div>
                    <reaction-counter :reply="storyBook.reply" />
                  </div>
                </div>
              </div>
              <div>
                {{ replyCount?.replyCount || 0 }}
                {{ $t("Overview.comments") }}
              </div>
              <reply-list
                :entity-id="storyBook?.id"
                :root-id="storyBook?.replyId"
                class="pb-[60px]"
                entity-type="App\Models\StoryBook"
                @on-reply-click="onReplyBtnClick"
                @on-reaction-click="onReplyActionClick"
              />
            </div>
          </VanTab>
          <!-- <VanTab :title="$t('story.tab.moment')" name="moment">
            <div
              v-if="momentList.length === 0"
              class="flex flex-col items-center justify-center h-[200px]"
            >
              <div class="flex flex-col items-center justify-center gap-2">
                <div class="text-2xl font-bold">
                  {{ $t("Message.noMomentsYet") }}
                </div>
                <div class="text-neutral-400">
                  {{ $t("Message.storyNoMoment") }}
                </div>
              </div>
            </div>
            <div v-else class="overflow-y-auto p-4 pb-[60px]">
              <div v-for="item in momentList" :key="item.id" class="border-b">
                <thread-item
                  :avatar-url="author!.mediaUrl"
                  :chat-message-list="item.chatMessagezz"
                  :content="item.text"
                  :enable-audio-play="false"
                  :enable-chat-message-click="false"
                  :enable-reaction-btn="false"
                  :enableTextCollapsed="false"
                  :from-mate="item.fromMate"
                  :impression-reactions="[]"
                  :mate-id="author!.uuid"
                  :mate-tone-id="author!.mateToneId"
                  :media-list="item.mediazz"
                  :mention-matezz="item.mentionMatezz ?? []"
                  :moment="item"
                  :show-avatar="true"
                  :show-comment="false"
                  :show-reaction="false"
                  :time="item.dtCreate"
                  :title="author!.name"
                  :type="author!.type"
                  :uuid="item.uuid"
                  :visibility="item.visibility"
                  class="border-b-0"
                  @thread-click="
                    router.push({
                      name: 'threadDetail',
                      params: { uuid: item.uuid },
                    })
                  "
                />
              </div>
            </div>
          </VanTab> -->
          <VanTab :title="$t('story.tab.gallery')" name="gallery">
            <div
              v-if="mediaList.length > 0"
              class="grid grid-cols-[repeat(auto-fill,minmax(100px,1fr))] gap-0.5 pb-[60px]"
            >
              <div
                v-for="(url, idx) in mediaList"
                :key="idx"
                class="w-full aspect-[1/1]"
                @click="
                  startPosition = idx;
                  showPreview = true;
                "
              >
                <div
                  v-if="isImage(url)"
                  class="bg-base-200 w-full h-full relative"
                >
                  <Image
                    :src="url"
                    class="w-full h-full"
                    fit="cover"
                    crossorigin="anonymous"
                  />
                </div>
                <div v-else class="w-full h-full bg-base-200 relative">
                  <Image
                    :src="url!"
                    class="w-full h-full"
                    fit="cover"
                    crossorigin="anonymous"
                  />
                  <div class="absolute right-1 bottom-1">
                    <i
                      class="iconify mingcute--video-camera-line text-base-content/50"
                    />
                  </div>
                </div>
              </div>
            </div>
          </VanTab>
        </VanTabs>
      </div>
    </div>
    <!-- @vue-ignore -->
    <moment-actions
      v-model:show="showActions"
      :moment="toOperateMoment"
      :reply="toOperateReply"
      :showEmoji="showEmoji"
    />
    <div
      class="flex gap-2 px-4 py-2 bg-base-200 border-t absolute bottom-0 left-0 right-0 z-[2]"
    >
      <div
        v-if="currentTab !== 'feedback' && storyList.length > 0"
        class="flex flex-1 gap-2"
      >
        <button class="btn flex-1 btn-neutral" @click="showMenu = true">
          {{ $t("story.selectChapter") }}
        </button>
        <button
          class="btn flex-1 btn-neutral"
          @click="
            router.push({
              name: 'readStory',
              params: { uuid: storyList[storyList.length - 1].uuid },
            })
          "
        >
          {{ $t("story.readBegin") }}
        </button>
      </div>

      <reply-input
        v-if="currentTab === 'feedback'"
        ref="replyInputRef"
        :entity-id="storyBook?.id"
        :reply-target="replyTarget"
        :entity-type="ReplyEntityType.StoryBook"
        :entity="storyBook"
      />
    </div>
    <mate-media-preview
      v-model:show="showPreview"
      :mate-media-list="previewMediaList"
      :show-media-info="false"
      :start-position="startPosition"
    />
    <VanPopup v-model:show="showMenu" class="h-[80%]" position="bottom" round>
      <div class="w-full h-full grid grid-rows-[auto_1fr] overflow-hidden">
        <div class="p-4 flex flex-col gap-4 border-b">
          <div
            class="flex gap-2 cursor-pointer"
            @click="
              router.push({
                name: 'storyBookHome',
                params: { uuid: currentStory?.storyBook?.uuid },
              })
            "
          >
            <div>
              <div class="text-base font-bold">
                {{ currentStory?.storyBook?.title }}
              </div>
              <div class="text-sm text-base-content/50">
                {{ author?.name }}
              </div>
            </div>
          </div>
          <div class="text-xs text-base-content/50">
            {{ $t("story.ongoing") }}
            {{ storyList.length }}
            {{ $t("story.ongoingTwo") }}
          </div>
        </div>
        <div class="overflow-y-auto">
          <VanCell
            v-for="(item, idx) in sortedStoryList"
            :key="item.id"
            :class="{
              'bg-primary text-primary': item.uuid === currentStory?.uuid,
            }"
            :title="`${idx + 1}. ${item.title}`"
            class="bg-transparent"
            is-link
            @click="onGoTargetStory(item.uuid)"
          />
        </div>
      </div>
    </VanPopup>
  </div>
</template>
